import DataApi from '../api/DataApi';
import { getCalculatedReturnType } from './reportCurrencyAggregate';

const KPI_LOG_PREFIX = '[KPI]';

/** Default KPI appearance (matches current dashboard styling). */
export const KPI_APPEARANCE_DEFAULTS = {
  alignment: 'left',
  background_color: '',
  title_color: '#5c6b7a',
  value_color: '#1a2332',
  positive_color: '#0d8050',
  negative_color: '#c23934',
};

const normalizeHexColor = (value) => {
  if (value == null) return '';
  const v = String(value).trim();
  if (!v) return '';
  if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(v)) return v;
  if (/^([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(v)) return `#${v}`;
  return '';
};

/** Read appearance color from widget_config (snake_case or camelCase). */
function readColorField(cfg, snakeKey) {
  if (!cfg || typeof cfg !== 'object') return '';
  const camelKey = snakeKey.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  return normalizeHexColor(cfg[snakeKey] ?? cfg[camelKey]);
}

/** Appearance fields stored on widget_config / dataset._metadata.appearance */
export function extractKpiAppearanceFields(cfg) {
  const c = normalizeKpiWidgetConfig(cfg);
  return {
    background_color: readColorField(c, 'background_color'),
    title_color: readColorField(c, 'title_color'),
    value_color: readColorField(c, 'value_color'),
    positive_color: readColorField(c, 'positive_color'),
    negative_color: readColorField(c, 'negative_color'),
  };
}

/** Parse widget_config from API (jsonb object or JSON string). */
export function normalizeKpiWidgetConfig(raw) {
  if (raw == null) return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof raw === 'object' ? { ...raw } : {};
}

export const isKpiCenterAlignment = (value) =>
  value === 'center' || value === true || value === 'true';

const isCenterAlignment = isKpiCenterAlignment;

/** Resolve appearance fields from widget_config (+ optional dataset metadata). */
export function resolveKpiWidgetStyles(widgetConfig, metadata) {
  const cfg = {
    ...normalizeKpiWidgetConfig(widgetConfig),
    ...(metadata?.appearance || {}),
  };
  const appearance = extractKpiAppearanceFields(cfg);
  return {
    alignment: isCenterAlignment(cfg.alignment) ? 'center' : 'left',
    backgroundColor: appearance.background_color || 'transparent',
    titleColor: appearance.title_color || KPI_APPEARANCE_DEFAULTS.title_color,
    valueColor: appearance.value_color || KPI_APPEARANCE_DEFAULTS.value_color,
    positiveColor:
      appearance.positive_color || KPI_APPEARANCE_DEFAULTS.positive_color,
    negativeColor:
      appearance.negative_color || KPI_APPEARANCE_DEFAULTS.negative_color,
  };
}

/** Trend direction from % change (primary) then API trend field. */
export function resolveKpiTrendKey(trend, changePercent) {
  const n = Number(changePercent);
  if (Number.isFinite(n)) {
    if (n > 0) return 'UP';
    if (n < 0) return 'DOWN';
    return 'FLAT';
  }
  const normalized = String(trend || '').toUpperCase();
  if (normalized === 'UP' || normalized === 'DOWN' || normalized === 'FLAT') {
    return normalized;
  }
  return 'FLAT';
}

export function getKpiTrendColor(styles, trendKey) {
  if (trendKey === 'UP') return styles.positiveColor;
  if (trendKey === 'DOWN') return styles.negativeColor;
  return '#706e6b';
}

/** Display title for a KPI tile (widget_config.label, then widget.name). */
export function getKpiWidgetLabel(widget) {
  const cfg = normalizeKpiWidgetConfig(widget?.widget_config);
  const fromConfig =
    typeof cfg.label === 'string' ? cfg.label.trim() : '';
  if (fromConfig) return fromConfig;
  const fromName =
    typeof widget?.name === 'string' ? widget.name.trim() : '';
  return fromName;
}

export function kpiClientLog(label, payload) {
  if (payload !== undefined) {
    console.log(`${KPI_LOG_PREFIX} ${label}`, payload);
  } else {
    console.log(`${KPI_LOG_PREFIX} ${label}`);
  }
}

/**
 * Execute a KPI widget on the server (report aggregate + comparison + cache).
 */
export async function executeKpiWidget(widgetId, options = {}) {
  const {
    viewAsUserId,
    forceRefresh = false,
    debug = true,
    filters = [],
    skipCache = false,
  } = options;
  const params = new URLSearchParams();
  if (viewAsUserId != null && viewAsUserId !== '') {
    params.set('viewAsUserId', viewAsUserId);
  }
  if (debug) {
    params.set('debug', '1');
  }
  const qs = params.toString() ? `?${params.toString()}` : '';

  kpiClientLog('executeKpiWidget.request', {
    widgetId,
    viewAsUserId,
    forceRefresh,
    url: `/widgets/${widgetId}/execute-kpi${qs}`,
  });

  const api = new DataApi('dashboard');
  let response;
  try {
    response = await api.post(`/widgets/${widgetId}/execute-kpi${qs}`, {
      forceRefresh,
      viewAsUserId: viewAsUserId || undefined,
      debug: debug === true,
      filters: Array.isArray(filters) ? filters : [],
      skipCache: skipCache === true,
    });
  } catch (err) {
    kpiClientLog('executeKpiWidget.networkError', {
      message: err?.message,
      response: err?.response?.data,
      status: err?.response?.status,
    });
    throw err;
  }

  kpiClientLog('executeKpiWidget.response', {
    success: response?.data?.success,
    message: response?.data?.message,
    data: response?.data?.data,
    debug: response?.data?.data?.debug,
  });

  if (!response?.data?.success) {
    const msg = response?.data?.message || 'Failed to execute KPI widget';
    kpiClientLog('executeKpiWidget.failed', { msg, full: response?.data });
    throw new Error(msg);
  }
  return response.data.data;
}

/**
 * Preview KPI for unsaved widget (preview-* id on canvas).
 */
export async function executeKpiPreview(dashboardId, options = {}) {
  const {
    parentId,
    widgetConfig,
    viewAsUserId,
    forceRefresh = false,
    debug = true,
    filters = [],
  } = options;

  if (!dashboardId || !parentId) {
    throw new Error('dashboardId and parentId are required for KPI preview');
  }
  if (!widgetConfig?.metric_field) {
    throw new Error('widget_config.metric_field is required for KPI preview');
  }

  const params = new URLSearchParams();
  if (viewAsUserId != null && viewAsUserId !== '') {
    params.set('viewAsUserId', viewAsUserId);
  }
  if (debug) {
    params.set('debug', '1');
  }
  const qs = params.toString() ? `?${params.toString()}` : '';

  kpiClientLog('executeKpiPreview.request', {
    dashboardId,
    parentId,
    widgetConfig,
    url: `/${dashboardId}/widgets/preview-kpi${qs}`,
  });

  const api = new DataApi('dashboard');
  const response = await api.post(`/${dashboardId}/widgets/preview-kpi${qs}`, {
    parent_id: parentId,
    widget_config: widgetConfig,
    forceRefresh,
    debug: debug === true,
    filters: Array.isArray(filters) ? filters : [],
  });

  kpiClientLog('executeKpiPreview.response', response?.data);

  if (!response?.data?.success) {
    throw new Error(response?.data?.message || 'Failed to preview KPI');
  }
  return response.data.data;
}

/**
 * Preview KPI for home page widgets (homewidgets API).
 */
export async function executeHomeKpiPreview(options = {}) {
  const {
    parentId,
    widgetConfig,
    viewAsUserId,
    forceRefresh = false,
    debug = true,
    filters = [],
  } = options;

  if (!parentId) {
    throw new Error('parentId is required for home KPI preview');
  }
  if (!widgetConfig?.metric_field) {
    throw new Error('widget_config.metric_field is required for home KPI preview');
  }

  const params = new URLSearchParams();
  if (viewAsUserId != null && viewAsUserId !== '') {
    params.set('viewAsUserId', viewAsUserId);
  }
  if (debug) {
    params.set('debug', '1');
  }
  const qs = params.toString() ? `?${params.toString()}` : '';

  const api = new DataApi('homewidgets');
  const response = await api.post(`/preview-kpi${qs}`, {
    parent_id: parentId,
    widget_config: widgetConfig,
    forceRefresh,
    debug: debug === true,
    filters: Array.isArray(filters) ? filters : [],
  });

  if (!response?.data?.success) {
    throw new Error(response?.data?.message || 'Failed to preview home KPI');
  }
  return response.data.data;
}

export function isPreviewKpiWidget(widget) {
  if (!widget) return false;
  return (
    widget.isPreview === true ||
    (widget.id != null && String(widget.id).startsWith('preview-'))
  );
}

/**
 * Execute all KPI widgets on a dashboard.
 */
export async function executeDashboardKpiWidgets(dashboardId, options = {}) {
  const { viewAsUserId, forceRefresh = false } = options;
  const qs =
    viewAsUserId != null && viewAsUserId !== ''
      ? `?viewAsUserId=${encodeURIComponent(viewAsUserId)}`
      : '';
  const api = new DataApi('dashboard');
  const response = await api.post(
    `/${dashboardId}/widgets/execute-kpis${qs}`,
    { forceRefresh, viewAsUserId: viewAsUserId || undefined }
  );
  if (!response?.data?.success) {
    throw new Error(response?.data?.message || 'Failed to execute KPI widgets');
  }
  return response.data.data;
}

export function isKpiDatasetValid(dataset) {
  if (!dataset || typeof dataset !== 'object') return false;
  return dataset.value != null || dataset.formatted_value != null;
}

/** Numeric / aggregatable types (aligned with FieldSelector summary options). */
export const KPI_NUMERIC_DATA_TYPES = [
  'number',
  'integer',
  'decimal',
  'currency',
  'percent',
  'percentage',
  'rollupsummary',
  'autonumber',
  'numeric',
  'float',
  'double',
  'real',
  'bigint',
  'smallint',
  'money',
  'calculated',
];

export const KPI_DATE_DATA_TYPES = ['date', 'datetime', 'timestamp'];

export function normalizeReportFieldForKpi(field) {
  if (!field) return null;
  const fieldName = field.field_name || field.name;
  if (!fieldName) return null;
  return {
    id: field.field_config_id || field.id || field.report_field_id,
    field_name: fieldName,
    field_label: field.field_label || field.label || fieldName,
    data_type: field.data_type,
    display_config: field.display_config ?? field.displayConfig,
    object_name: field.object_name,
    is_aggregated: field.is_aggregated,
    is_grouped: field.is_grouped,
  };
}

export function isKpiNumericDataType(dataType, fieldMeta = null) {
  const dt = String(dataType || '').toLowerCase();
  if (dt === 'calculated') {
    const rt = getCalculatedReturnType(fieldMeta || { data_type: dt });
    return ['number', 'currency', 'percent'].includes(rt);
  }
  return KPI_NUMERIC_DATA_TYPES.includes(dt);
}

export function isKpiMetricEligibleField(field, aggregation = 'SUM') {
  const normalized = normalizeReportFieldForKpi(field);
  if (!normalized) return false;
  const agg = String(aggregation || 'SUM').toUpperCase();

  if (agg === 'COUNT') {
    return !normalized.is_grouped;
  }

  if (isKpiNumericDataType(normalized.data_type, normalized)) {
    return true;
  }
  if (normalized.is_aggregated && !normalized.is_grouped) {
    return true;
  }
  return false;
}

/**
 * Fields eligible for KPI metric_field dropdown.
 */
export function getKpiMetricFieldCandidates(reportFields, aggregation = 'SUM') {
  const normalized = (reportFields || [])
    .map(normalizeReportFieldForKpi)
    .filter(Boolean);

  let candidates = normalized.filter((f) =>
    isKpiMetricEligibleField(f, aggregation)
  );

  if (candidates.length === 0) {
    candidates = normalized.filter((f) => !f.is_grouped);
  }

  const seen = new Set();
  return candidates.filter((f) => {
    const key = `${f.object_name || ''}:${f.field_name}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function getKpiDateFieldCandidates(reportFields) {
  const normalized = (reportFields || [])
    .map(normalizeReportFieldForKpi)
    .filter(Boolean);

  return normalized.filter((f) =>
    KPI_DATE_DATA_TYPES.includes(String(f.data_type || '').toLowerCase())
  );
}

export function shouldRefreshKpiCache(widget, dashboard, currentRunUserId) {
  if ((dashboard?.view_type || '').toUpperCase() !== 'LOGGED_IN_USER') {
    return false;
  }
  const savedRunUserId = widget?.dataset?._metadata?.run_user_id || null;
  if (!currentRunUserId) return false;
  return !savedRunUserId || savedRunUserId !== currentRunUserId;
}
